import { useCaseFinderView } from "@/api/contacts/useContacts";
import { labelOf, PRIVACY_PROVISION_LABEL } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { EmptyState } from "@/ui/components/EmptyState";
import { InfoField } from "@/ui/components/DenseRow";
import { SoftSection } from "@/ui/components/SoftSection";
import { FINDER_ON_CONTACT_STAND } from "@/ui/finder/finderMode";
import { formatDateDE } from "@/utils/isoDate";
import { Text, View } from "react-native";

function documentedOn(iso: string | null | undefined): string {
  if (!iso) return "";
  const formatted = formatDateDE(iso);
  return formatted === "–" ? "" : formatted;
}

function documentedValue(
  version: string | null | undefined,
  documentedAt: string | null | undefined,
): string | null {
  const trimmed = version?.trim() ?? "";
  const when = documentedOn(documentedAt);
  if (!trimmed && !when) return null;
  if (trimmed && when) return `Version ${trimmed} · dokumentiert ${when}`;
  if (trimmed) return `Version ${trimmed}`;
  return `dokumentiert ${when}`;
}

export function CaseFinderSection({ caseId }: { caseId: string }) {
  const { theme } = useTheme();
  const { view, loading } = useCaseFinderView(caseId);

  if (loading && !view) {
    return (
      <SoftSection title="Finder">
        <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
          Laden…
        </Text>
      </SoftSection>
    );
  }

  if (!view || !view.has_finder) {
    return (
      <SoftSection title="Finder">
        <EmptyState
          title="Kein Finder dokumentiert"
          hint="Kontaktdaten erscheinen hier, sobald ein Finder hinterlegt ist."
        />
      </SoftSection>
    );
  }

  if (!view.can_read_pii) {
    return (
      <SoftSection title="Finder">
        <Text
          style={{
            color: theme.color.textSecondary,
            fontSize: theme.fontSize.small,
          }}
        >
          Finder dokumentiert
        </Text>
      </SoftSection>
    );
  }

  const documentedText = documentedValue(
    view.privacy_notice_version,
    view.privacy_documented_at,
  );
  const hasContactStand = Boolean(view.privacy_provision_kind || documentedText);
  const standId = `finder-contact-stand-${caseId}`;
  const provision = (
    <InfoField
      label="Art der Bereitstellung"
      value={labelOf(PRIVACY_PROVISION_LABEL, view.privacy_provision_kind)}
    />
  );
  const documented = documentedText ? (
    <InfoField label="Datenschutzinformation" value={documentedText} />
  ) : null;

  return (
    <SoftSection title="Finder">
      <View style={{ gap: theme.space.sm }}>
        {view.is_active === false ? (
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}>
            Kontakt deaktiviert – historisch verknüpft
          </Text>
        ) : null}
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: theme.space.md,
          }}
        >
          <InfoField label="Name" value={view.display_name ?? ""} />
          <InfoField label="Telefon" value={view.phone ?? ""} />
          <InfoField label="E-Mail" value={view.email ?? ""} />
          <InfoField label="Anschrift" value={view.address ?? ""} />
          {view.notes?.trim() ? (
            <InfoField label="Hinweise" value={view.notes.trim()} />
          ) : null}
          {hasContactStand ? null : provision}
          {hasContactStand ? null : documented}
        </View>
        {hasContactStand ? (
          <View
            role="group"
            aria-labelledby={standId}
            style={{
              gap: theme.space.sm,
              padding: theme.space.sm,
              borderRadius: theme.radius.md,
              backgroundColor: theme.color.infoBg,
            }}
          >
            <Text
              nativeID={standId}
              accessibilityRole="header"
              style={{
                color: theme.color.infoText,
                fontSize: theme.fontSize.body,
                fontWeight: "700",
              }}
            >
              {FINDER_ON_CONTACT_STAND}
            </Text>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: theme.space.md,
              }}
            >
              {provision}
              {documented}
            </View>
          </View>
        ) : null}
      </View>
    </SoftSection>
  );
}
